import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../translations/LanguageContext';

const FORMSPREE_CONTACT_ENDPOINT = 'https://formspree.io/f/xvzkkjla';

const Contact = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(FORMSPREE_CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          subject: formData.subject,
          message: formData.message,
        }),
      });

      if (response.ok) {
        toast.success(t('contact.success') || 'Message sent successfully! We will contact you soon.');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error(t('contact.error') || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2e5453]">
      {/* Hero */}
      <section className="bg-[#3d6a68] py-16">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-[#96ba1e] text-[#2e5453] hover:bg-[#a8c82e] mb-4">
            {t('contact.badge') || 'CONTACT US'}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#96ba1e]">{t('contact.title') || 'Get In Touch'}</h1>
          <p className="text-xl text-[#c4d88f] max-w-2xl mx-auto">
            {t('contact.subtitle') || "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible."}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-[#2e5453]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="hover:shadow-lg transition-shadow bg-[#3d6a68] border-[#96ba1e] border">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#2e5453] rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-7 h-7 text-[#96ba1e]" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-[#96ba1e]">{t('contact.visitUs') || 'Visit Us'}</h3>
                  <p className="text-[#c4d88f] text-sm leading-relaxed">
                    {t('contact.address') || 'Burgas, Bulgaria'}
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow bg-[#3d6a68] border-[#96ba1e] border">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#2e5453] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-7 h-7 text-[#96ba1e]" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-[#96ba1e]">{t('contact.callUs') || 'Call Us'}</h3>
                  <p className="text-[#c4d88f] text-sm">
                    +359 XXX XXX XXX
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow bg-[#3d6a68] border-[#96ba1e] border">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#2e5453] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-[#96ba1e]" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-[#96ba1e]">{t('contact.emailUs') || 'Email Us'}</h3>
                  <p className="text-[#c4d88f] text-sm">
                    myinferno@gmail.com<br />
                    {t('contact.replyTime') || 'We reply within 24 hours'}
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow bg-[#3d6a68] border-[#96ba1e] border">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#2e5453] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-[#96ba1e]" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-[#96ba1e]">{t('contact.openingHours') || 'Opening Hours'}</h3>
                  <p className="text-[#c4d88f] text-sm leading-relaxed">
                    {t('contact.workingHours') || '12:00 - 22:00'}<br />
                    {t('contact.includesWeekends') || 'Including weekends'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-[#3d6a68] border-[#96ba1e] border">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold mb-6 text-[#96ba1e]">{t('contact.formTitle') || 'Send Us a Message'}</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-[#96ba1e]">{t('contact.yourName') || 'Your Name'} *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-[#96ba1e]">{t('contact.emailAddress') || 'Email Address'} *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone" className="text-[#96ba1e]">{t('contact.phoneNumber') || 'Phone Number'}</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-[#96ba1e]">{t('contact.subject') || 'Subject'} *</Label>
                        <Input
                          id="subject"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          required
                          className="mt-2 bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#96ba1e]">{t('contact.message') || 'Message'} *</Label>
                      <textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        rows="6"
                        className="w-full mt-2 p-3 border rounded-lg resize-none bg-[#2e5453] border-[#96ba1e] text-[#c4d88f] placeholder:text-[#6a9a99]"
                        placeholder={t('contact.messagePlaceholder') || 'Tell us how we can help you...'}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-[#96ba1e] hover:bg-[#a8c82e] text-[#2e5453] font-bold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (t('contact.sending') || 'Sending...') : (t('contact.sendMessage') || 'Send Message')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Updated coordinates: 42.522414, 27.440854 (Burgas, Bulgaria) */}
      <section className="py-16 bg-[#3d6a68]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <Card className="bg-[#2e5453] border-[#96ba1e] border">
              <CardContent className="p-0">
                <div className="aspect-video bg-[#2e5453] rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2932.5!2d27.440854!3d42.522414!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDMxJzIwLjciTiAyN8KwMjYnMjcuMSJF!5e0!3m2!1sen!2sbg!4v1704067200000!5m2!1sen!2sbg"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Children Planet Location"
                  ></iframe>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
